import { useCallback, useEffect, useRef, useState } from "react";

import { previewEmail } from "../services/onboarding/emailPreview";
import { EmailPreview, EmailPreviewType } from "../types/onboarding";

/**
 * The email a send action would deliver, fetched while `enabled` (CWA-173).
 *
 * Refetches when the type or the station selection changes — compared by value,
 * so a caller passing a fresh array with the same stations fetches nothing. Only
 * the newest request may set state: ticking stations quickly fires several, and
 * one answering late must not replace the preview of the current selection.
 */
const useEmailPreview = (
  prospectId: string,
  type: EmailPreviewType,
  stationIds: string[] | undefined,
  enabled: boolean
) => {
  const [preview, setPreview] = useState<EmailPreview | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<any>(null);
  const [attempt, setAttempt] = useState(0);
  const latest = useRef(0);

  const stationKey = stationIds?.join("\u0000") ?? "";

  useEffect(() => {
    if (!enabled) {
      // Dropped on close so a reopened dialog never shows the previous email
      // with an enabled Send while the new one loads.
      latest.current++;
      setPreview(null);
      setError(null);
      setLoading(false);
      return;
    }
    const request = ++latest.current;
    setLoading(true);
    setError(null);
    previewEmail(prospectId, type, stationIds)
      .then((result) => {
        if (request === latest.current) setPreview(result);
      })
      .catch((err) => {
        if (request !== latest.current) return;
        setPreview(null);
        setError(err);
      })
      .finally(() => {
        if (request === latest.current) setLoading(false);
      });
    // stationKey stands in for stationIds, compared by value.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [prospectId, type, stationKey, enabled, attempt]);

  const retry = useCallback(() => setAttempt((n) => n + 1), []);

  return { preview, loading, error, retry };
};

export default useEmailPreview;
